import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Outlet,
  Scripts,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";
import { HelmetProvider } from "react-helmet-async";

import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { CookieConsent } from "@/components/CookieConsent";
import { ScrollToTop } from "@/components/ScrollToTop";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import appCss from "../styles.css?url";

// ported from index.html — JSON-LD structured data
const organizationLd = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": "https://cargonova.ai/#organization",
  name: "CargoNova",
  alternateName: ["Cargo Nova", "CargoNova AI", "Cargo Nova AI", "卡狮智运"],
  url: "https://cargonova.ai/",
  logo: "https://cargonova.ai/logo.png",
  description:
    "CargoNova AI is an AI-native freight forwarding software platform by Deep Insights",

  parentOrganization: {
    "@type": "Organization",
    name: "Deep Insights",
    url: "https://www.deep-insights.ai/",
  },
  sameAs: [
    "https://www.linkedin.com/company/deep-insights-ai",
    "https://www.linkedin.com/company/deep-insights-technology/",
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Shanghai",
    addressRegion: "Shanghai",
    addressCountry: "CN",
  },
  areaServed: "Worldwide",
});

const websiteLd = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "@id": "https://cargonova.ai/#website",
  name: "CargoNova",
  alternateName: ["Cargo Nova", "CargoNova AI"],
  url: "https://cargonova.ai/",
  description:
    "CargoNova AI — AI-native freight forwarding software by Deep Insights",

  publisher: { "@id": "https://cargonova.ai/#organization" },
  inLanguage: "en",
});


export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      {
        name: "keywords",
        content:
          "freight forwarding software, freight forwarding system, freight forwarding platform, freight forwarder system, freight forwarder platform, AI FMS system, AI FMS platform, FMS system, FMS platform, AI freight platform, digital freight forwarder, ocean freight software, air freight software",
      },
      { name: "author", content: "CargoNova" },
      {
        name: "google-site-verification",
        content: "XydwwrpnmEoeUsrD7gHzTxDiUZkuAZIj0l9NLp3liiA",
      },
      {
        name: "bytedance-verification-code",
        content: "X9h2AUzyJBaX3VtU5RVO",
      },
      { property: "og:site_name", content: "CargoNova" },
      { property: "og:image", content: "https://cargonova.ai/og-image.jpg" },
      { property: "og:image:width", content: "1216" },
      { property: "og:image:height", content: "640" },
      {
        property: "og:image:alt",
        content: "CargoNova — AI-Native Freight Forwarding Platform",
      },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://cargonova.ai/og-image.jpg" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "shortcut icon", href: "/favicon.ico", type: "image/x-icon" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", href: "/favicon-32.png", type: "image/png", sizes: "32x32" },
      { rel: "icon", href: "/favicon.png", type: "image/png", sizes: "48x48" },
      { rel: "icon", href: "/favicon-96.png", type: "image/png", sizes: "96x96" },
      { rel: "icon", href: "/favicon-128.png", type: "image/png", sizes: "128x128" },
      { rel: "icon", href: "/favicon-192.png", type: "image/png", sizes: "192x192" },
      { rel: "icon", href: "/favicon.svg", type: "image/svg+xml" },
      { rel: "apple-touch-icon", href: "/apple-touch-icon.png", sizes: "180x180" },
    ],
    scripts: [
      { type: "application/ld+json", children: organizationLd },
      { type: "application/ld+json", children: websiteLd },
    ],

  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const lang = pathname.startsWith("/zh")
    ? "zh-CN"
    : pathname.startsWith("/th")
    ? "th-TH"
    : "en";
  return (
    <html lang={lang} suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  useEffect(() => {
    document.documentElement.classList.add("hydrated");
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <LanguageProvider>
            <ScrollToTop />
            <Outlet />
            <CookieConsent />
          </LanguageProvider>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md text-center">
        <h1 className="mb-2 text-xl font-semibold text-foreground">This page didn't load</h1>
        <p className="mb-6 text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-2">
          <button
            className="rounded-md bg-primary px-4 py-2 text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            className="rounded-md border border-border bg-card px-4 py-2 text-foreground"
            href="/"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
